import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import athleteService from '../services/athleteService';

export const MEDAL_COLORS = { gold: '#eda100', silver: '#9aa2ad', bronze: '#b0703c' };
export const chartTextStyle = { fontSize: 12, fill: '#5b6472' };

// 比較單位的標示色，最多同時比較 6 個單位
export const SELECT_COLORS = [
  '#1d63b8', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#4a3aa7',
];
export const MAX_COMPARE = 6;

export const sumTotals = (yearly) =>
  yearly.reduce(
    (acc, y) => ({
      gold: acc.gold + y.gold,
      silver: acc.silver + y.silver,
      bronze: acc.bronze + y.bronze,
      top8: acc.top8 + y.top8,
      entries: acc.entries + y.entries,
    }),
    { gold: 0, silver: 0, bronze: 0, top8: 0, entries: 0 }
  );

export const StatCards = ({ totals }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: 1.5,
      p: 1.5,
      borderRadius: 2,
      bgcolor: '#f8fafc',
      border: 1,
      borderColor: 'divider',
      mb: 2,
    }}
  >
    {[
      ['金牌', totals.gold],
      ['銀牌', totals.silver],
      ['銅牌', totals.bronze],
      ['前八名', totals.top8],
      ['參賽人次', totals.entries],
    ].map(([label, value]) => (
      <Box key={label} sx={{ textAlign: 'center' }}>
        <Typography variant="h6">{value}</Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    ))}
  </Box>
);

export const YearlyChart = ({ yearly, height = 260 }) => (
  <Box sx={{ width: '100%', height }}>
    <ResponsiveContainer>
      <BarChart data={yearly} margin={{ top: 8, right: 12, bottom: 4, left: 0 }} barCategoryGap="30%">
        <CartesianGrid stroke="#eceff3" vertical={false} />
        <XAxis dataKey="year" tick={chartTextStyle} axisLine={{ stroke: '#d5dae1' }} tickLine={false} />
        <YAxis allowDecimals={false} tick={chartTextStyle} axisLine={false} tickLine={false} width={32} />
        <Tooltip labelFormatter={(year) => `${year} 年`} />
        <Legend wrapperStyle={{ fontSize: 13 }} />
        <Bar dataKey="gold" name="金牌" stackId="m" fill={MEDAL_COLORS.gold} />
        <Bar dataKey="silver" name="銀牌" stackId="m" fill={MEDAL_COLORS.silver} />
        <Bar dataKey="bronze" name="銅牌" stackId="m" fill={MEDAL_COLORS.bronze} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </Box>
);

// 選手歷年成績彈窗：點主力選手姓名時開啟，不用離開統計頁
const AthleteHistoryDialog = ({ name, open, onClose, onSearchAthlete }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !name) return;
    let mounted = true;
    setLoading(true);
    setRows([]);
    athleteService
      .searchGameResults({ athlete_name: name })
      .then((res) => {
        if (!mounted || !res.success) return;
        const list = res.data
          .filter((r) => r.Athlete_Name === name)
          .sort((a, b) => {
            if ((b.GamesYear || 0) !== (a.GamesYear || 0)) {
              return (b.GamesYear || 0) - (a.GamesYear || 0);
            }
            const ra = parseInt(a.Ranking, 10) || 999;
            const rb = parseInt(b.Ranking, 10) || 999;
            return ra - rb;
          });
        setRows(list.slice(0, 50));
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [open, name]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{name} 歷年成績</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        )}
        {!loading && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            查無成績資料
          </Typography>
        )}
        {!loading && rows.length > 0 && (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>年份</TableCell>
                  <TableCell>賽事</TableCell>
                  <TableCell>競賽項目</TableCell>
                  <TableCell>名次</TableCell>
                  <TableCell>成績</TableCell>
                  <TableCell>單位</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{r.GamesYear || '-'}</TableCell>
                    <TableCell>{r.GamesName || '-'}</TableCell>
                    <TableCell>{r.Competition_Name || '-'}</TableCell>
                    <TableCell>
                      {r.Ranking ? (
                        <Chip
                          label={`第${r.Ranking}名`}
                          size="small"
                          color={String(r.Ranking).trim() === '1' ? 'primary' : 'default'}
                          variant={parseInt(r.Ranking, 10) <= 3 ? 'filled' : 'outlined'}
                          sx={{ height: 20, fontSize: 12 }}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{r.Score || '-'}</TableCell>
                    <TableCell>{r.SchoolName || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
            if (onSearchAthlete) onSearchAthlete(name);
          }}
        >
          到查詢頁看完整資料
        </Button>
        <Button variant="contained" onClick={onClose}>
          關閉
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// 可點擊的選手姓名：點了開啟歷年成績彈窗
export const AthleteNameLink = ({ name, onSearchAthlete, fontSize = 15 }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Link
        component="button"
        type="button"
        underline="hover"
        sx={{ fontWeight: 700, fontSize }}
        onClick={() => setOpen(true)}
      >
        {name}
      </Link>
      {open && (
        <AthleteHistoryDialog
          name={name}
          open={open}
          onClose={() => setOpen(false)}
          onSearchAthlete={onSearchAthlete}
        />
      )}
    </>
  );
};

// 完整版主力選手表格（單一單位檢視用）
export const AthleteTable = ({ athletes, onSearchAthlete }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>姓名</TableCell>
          <TableCell>最佳名次</TableCell>
          <TableCell>獎牌數</TableCell>
          <TableCell>參賽次數</TableCell>
          <TableCell>主要項目</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {athletes.map((a) => (
          <TableRow key={a.name} hover>
            <TableCell>
              <AthleteNameLink name={a.name} onSearchAthlete={onSearchAthlete} fontSize={14} />
            </TableCell>
            <TableCell>
              {a.best_ranking ? (
                <Chip
                  label={`第${a.best_ranking}名`}
                  size="small"
                  color={a.best_ranking === 1 ? 'primary' : 'default'}
                  variant={a.best_ranking <= 3 ? 'filled' : 'outlined'}
                />
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>{a.medals}</TableCell>
            <TableCell>{a.entries}</TableCell>
            <TableCell>{a.events.join('、') || '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// 精簡版主力選手（比較模式用）：一列一人，姓名與數據同行、項目縮在第二行
const AthleteCompactList = ({ athletes, onSearchAthlete }) => (
  <Box>
    {athletes.map((a) => {
      const events = a.events.slice(0, 3).join('、');
      const more = a.events.length > 3 ? ' 等' : '';
      return (
        <Box
          key={a.name}
          sx={{
            py: 1,
            px: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            '&:last-child': { borderBottom: 0 },
            '&:hover': { bgcolor: '#f8fafc' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AthleteNameLink name={a.name} onSearchAthlete={onSearchAthlete} />
            {a.best_ranking && (
              <Chip
                label={`第${a.best_ranking}名`}
                size="small"
                color={a.best_ranking === 1 ? 'primary' : 'default'}
                variant={a.best_ranking <= 3 ? 'filled' : 'outlined'}
                sx={{ height: 20, fontSize: 12 }}
              />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
              獎牌 {a.medals}・參賽 {a.entries}
            </Typography>
          </Box>
          {events && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mt: 0.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {events}
              {more}
            </Typography>
          )}
        </Box>
      );
    })}
  </Box>
);

const COMPARE_METRICS = [
  ['gold', '金牌數'],
  ['medals', '獎牌總數'],
  ['top8', '前八名'],
  ['entries', '參賽人次'],
];

const metricOf = (y, metric) =>
  metric === 'medals' ? (y.gold || 0) + (y.silver || 0) + (y.bronze || 0) : (y[metric] || 0);

// 多單位並排比較（2 到 6 個）：總計對照表＋歷年折線圖＋精簡主力選手
export const CompareView = ({ units, details, onSearchAthlete }) => {
  const [metric, setMetric] = useState('gold');

  const chartData = useMemo(() => {
    const yearsSet = new Set(
      units.flatMap((u) => (details[u]?.yearly || []).map((y) => y.year))
    );
    return [...yearsSet].sort().map((year) => {
      const row = { year };
      units.forEach((u) => {
        const y = (details[u]?.yearly || []).find((it) => it.year === year);
        if (y) row[u] = metricOf(y, metric);
      });
      return row;
    });
  }, [units, details, metric]);

  const metricLabel = COMPARE_METRICS.find(([k]) => k === metric)?.[1];

  return (
    <Box>
      {/* 總計對照表 */}
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              {units.map((unit, i) => (
                <TableCell
                  key={unit}
                  align="center"
                  sx={{ color: SELECT_COLORS[i % SELECT_COLORS.length], fontSize: 15 }}
                >
                  {unit}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              ['金牌', 'gold'],
              ['銀牌', 'silver'],
              ['銅牌', 'bronze'],
              ['前八名', 'top8'],
              ['參賽人次', 'entries'],
            ].map(([label, key]) => {
              const values = units.map((unit) =>
                details[unit]?.yearly?.length
                  ? sumTotals(details[unit].yearly)[key]
                  : 0
              );
              const max = Math.max(...values);
              return (
                <TableRow key={key} hover>
                  <TableCell sx={{ color: 'text.secondary' }}>{label}</TableCell>
                  {values.map((v, i) => (
                    <TableCell
                      key={units[i]}
                      align="center"
                      sx={{ fontWeight: v === max && v > 0 ? 700 : 400, fontSize: 15 }}
                    >
                      {v}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 歷年趨勢折線圖，指標可切換 */}
      {chartData.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="subtitle2">歷年{metricLabel}趨勢</Typography>
            <TextField
              select
              size="small"
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              sx={{ width: 140, ml: 'auto' }}
            >
              {COMPARE_METRICS.map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ width: '100%', height: 280, mb: 2 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#eceff3" vertical={false} />
                <XAxis dataKey="year" tick={chartTextStyle} axisLine={{ stroke: '#d5dae1' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={chartTextStyle} axisLine={false} tickLine={false} width={32} />
                <Tooltip labelFormatter={(year) => `${year} 年`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {units.map((unit, i) => (
                  <Line
                    key={unit}
                    type="monotone"
                    dataKey={unit}
                    stroke={SELECT_COLORS[i % SELECT_COLORS.length]}
                    strokeWidth={2}
                    connectNulls
                    dot={{ r: 3, strokeWidth: 2, fill: '#fff' }}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}

      {/* 主力選手並排（精簡版） */}
      <Grid container spacing={2}>
        {units.map((unit, i) => (
          <Grid item xs={12} md={6} lg={units.length > 2 ? 4 : 6} key={unit}>
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  bgcolor: '#f8fafc',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" sx={{ color: SELECT_COLORS[i % SELECT_COLORS.length] }}>
                  {unit} 主力選手
                </Typography>
              </Box>
              {details[unit]?.athletes?.length > 0 ? (
                <AthleteCompactList
                  athletes={details[unit].athletes.slice(0, 5)}
                  onSearchAthlete={onSearchAthlete}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                  無資料
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
